import { Link, useLocation } from 'react-router';
import { Icon } from '../../components/icons';
import { Differentiators } from '../../components/public/Differentiators';
import { NeuralField } from '../../components/public/NeuralField';
import { PricingNote } from '../../components/public/PricingNote';
import { PublicStats } from '../../components/public/PublicStats';
import { PublicShell } from '../../components/public/PublicShell';
import { Reveal } from '../../components/public/Reveal';
import { landing } from '../../content/landing';
import { loginHref } from '../../lib/loginHref';

/**
 * Trang giới thiệu public — thứ người CHƯA đăng nhập thấy ở mọi địa chỉ panel.
 *
 * TOÀN BỘ chữ và link nằm ở `content/landing.ts`, không có chuỗi nào khai ở đây.
 * Trang này cố ý chỉ có tiếng Anh (khách ghé từ mọi nơi), không dùng hệ dict()
 * hai thứ tiếng của panel.
 */
export default function Landing(): React.JSX.Element {
  // Trang này là thứ người CHƯA đăng nhập thấy ở mọi địa chỉ panel, nên đường
  // dẫn hiện tại có thể là `/rewards/grant` chứ không riêng `/`. Đăng nhập xong
  // phải về đúng chỗ đó — xem `lib/loginHref.ts`.
  const location = useLocation();
  const href = loginHref(location.pathname);

  return (
    <PublicShell wide>
      <LandingBody loginHref={href} />
    </PublicShell>
  );
}

/**
 * Phần giới thiệu, tách ra để TRANG CHỦ dùng lại.
 *
 * Trang chủ (`pages/Home.tsx`) là cùng nội dung này cộng thêm lưới server đang
 * quản lý ở trên. Chép lại thay vì tách thì hai bản sẽ lệch ở lần sửa câu chữ
 * tiếp theo — mà đây là câu chữ bán hàng, tức là thứ được sửa thường xuyên nhất.
 *
 * `loginHref` bỏ trống = người xem ĐÃ đăng nhập: bỏ cặp nút hero (họ đã ở trong
 * rồi), bỏ dòng "vừa mời bot xong?", bỏ luôn tiêu đề `h1` "GenSolva" (thương
 * hiệu đã ở header dính, và trên trang chủ `h1` thật là tiêu đề khu làm việc —
 * hai `h1` trên một trang là sai a11y) và hiệu ứng `.rise` vào trang (nền
 * chuyển động vĩnh viễn hợp với trang giới thiệu, không hợp với chỗ làm việc
 * hằng ngày — cùng lý do canvas bị bỏ ngay dưới đây). Vẫn giữ lưới tính năng
 * và bảng giá, vốn luôn đáng đọc.
 */
export function LandingBody({ loginHref }: { loginHref?: string }): React.JSX.Element {
  const signedIn = loginHref === undefined;

  return (
    <>
      {/*
        Hero: `relative isolate` để hai lớp nền (canvas chòm sao + quầng sáng)
        xếp dưới chữ bằng z âm mà không lọt ra ngoài section. Canvas chỉ vẽ ở
        trang public — trang chủ của người ĐÃ đăng nhập là chỗ làm việc hằng
        ngày, một hình nền chuyển động vĩnh viễn ở đó là tiếng ồn. Trang public
        thì NGƯỢC LẠI: canvas, quầng sáng và hiệu ứng vào trang giữ nguyên —
        đây là thứ khách ghé lần đầu nhìn thấy trước tiên.

        Các khối chữ vào trang so le bằng `.rise` + animation-delay: một nhịp
        dàn dựng duy nhất lúc mở trang, thay vì rải hiệu ứng khắp nơi.
      */}
      <section className={`relative isolate overflow-hidden ${signedIn ? '' : 'min-h-[60vh]'}`}>
        {!signedIn && <NeuralField />}
        {!signedIn && <div className="hero-glow" aria-hidden="true" />}
        <div
          className={`mx-auto px-4 ${signedIn ? 'max-w-5xl pb-8' : 'max-w-3xl pt-16 pb-12 text-center sm:pt-20'}`}
        >
          {!signedIn && (
            <h1 className="rise hero-title text-5xl font-extrabold tracking-tight sm:text-6xl">
              {landing.brand}
            </h1>
          )}
          <p
            className={
              signedIn
                ? 'text-xl font-medium text-accent'
                : 'rise mt-4 text-xl font-medium text-accent [animation-delay:120ms]'
            }
          >
            {landing.tagline}
          </p>
          <div
            className={`space-y-4 text-left text-base leading-relaxed text-fg-muted ${
              signedIn ? 'mt-4 max-w-3xl' : 'rise mt-6 [animation-delay:240ms] sm:text-center'
            }`}
          >
            {landing.intro.map((paragraph) => (
              <p key={paragraph.slice(0, 32)}>{paragraph}</p>
            ))}
          </div>

          {!signedIn && (
            <div className="rise [animation-delay:360ms]">
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <a
                  href={landing.buttons.addBot.url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-glow inline-flex w-full items-center justify-center rounded-xl bg-accent px-6 py-3 text-base font-semibold text-white hover:bg-accent-hover sm:w-auto"
                >
                  {landing.buttons.addBot.label}
                </a>
                <a
                  href={loginHref}
                  className="inline-flex w-full items-center justify-center rounded-xl border border-border-strong px-6 py-3 text-base font-semibold text-fg-muted backdrop-blur-sm hover:bg-surface-3 sm:w-auto"
                >
                  {landing.buttons.login.label}
                </a>
              </div>

              {/*
                Đường dẫn tới hướng dẫn đặt NGAY dưới nút mời bot, không chỉ trên thanh
                menu: người vừa bấm "Add to Discord" là người đang cần nó nhất, và họ
                đang nhìn vào đúng chỗ này.
              */}
              <p className="mt-4 text-sm text-fg-subtle">
                Just added the bot?{' '}
                <Link
                  to={landing.buttons.setup.url}
                  className="font-medium text-accent hover:underline"
                >
                  {landing.buttons.setup.label}
                </Link>
              </p>
            </div>
          )}
        </div>
      </section>

      {/*
        Khối khác biệt đứng TRƯỚC lưới tính năng — xem lý do ở
        `content/landing.ts`, mục `differentiators`.
      */}
      <section className="mx-auto max-w-5xl px-4 pt-2 pb-12">
        <Differentiators />
        {/*
          Dải số liệu bám NGAY dưới ba lời khẳng định, không đặt cuối trang: nó
          là bằng chứng cho chúng, và bằng chứng đặt cách lời khẳng định hai màn
          hình thì không còn ai nối được hai thứ với nhau. Tự ẩn khi chưa đủ điều
          kiện công bố — xem `PublicStats.tsx`.
        */}
        <div className="mt-6">
          <PublicStats />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-14">
        <Reveal>
          <h2 className="mb-5 text-xl font-semibold tracking-heading text-fg sm:text-2xl">
            {landing.featuresHeading}
          </h2>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/*
            Trễ theo CỘT (`index % 3`), không theo chỉ số tuyệt đối: mỗi hàng tự
            kích hoạt khi cuộn tới, nên trễ cộng dồn theo chỉ số chỉ làm hàng
            dưới ì ra vô cớ.
          */}
          {landing.features.map((feature, index) => (
            <Reveal key={feature.title} delay={(index % 3) * 90} className="h-full">
              <div className="card-glow h-full rounded-xl border border-border bg-surface p-5 shadow-sm">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-soft text-accent">
                  <Icon name={feature.icon} size={20} />
                </div>
                <h3 className="mt-2 text-base font-semibold">{feature.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-fg-muted">
                  {feature.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        {/*
          Cặp link đứng TRƯỚC bảng giá (đổi chỗ 16/08/2026): lời cuối của trang
          nên là khối giá, không phải điều hướng — bảng giá mới là câu chốt bán
          hàng, hai link chỉ là lối rẽ cho ai muốn đọc thêm.
        */}
        <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
          <Link
            to={landing.buttons.features.url}
            className="font-medium text-accent hover:underline"
          >
            {landing.buttons.features.label} →
          </Link>
          <Link
            to={landing.buttons.commands.url}
            className="font-medium text-accent hover:underline"
          >
            {landing.buttons.commands.label} →
          </Link>
        </div>

        <Reveal className="mt-10">
          <PricingNote />
        </Reveal>
      </section>
    </>
  );
}
